/**
 * TASK-COMBAT-VIZ-013: Projectile Renderer Implementation
 *
 * Renders animated projectiles for ranged attacks in combat.
 * Supports different projectile types with arcing trajectories and rotation.
 */

import * as PIXI from 'pixi.js';

/**
 * Projectile type enumeration
 */
export enum ProjectileType {
  ARROW = 'arrow',
  FIREBALL = 'fireball',
  ICE_SHARD = 'ice_shard',
  LIGHTNING = 'lightning',
  GENERIC = 'generic'
}

/**
 * Projectile renderer configuration
 */
export interface ProjectileConfig {
  speed?: number;        // Pixels per second
  arcHeight?: number;    // Maximum arc height in pixels
}

/**
 * Visual properties for projectile types
 */
export interface ProjectileProperties {
  color: number;
  size: number;
  hasArc: boolean;
}

/**
 * Projectile data structure
 */
export interface Projectile {
  graphics: PIXI.Graphics;
  type: ProjectileType;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  startTime: number;
  progress: number;
  isComplete: boolean;
}

/**
 * Visual projectile for state-based rendering
 * Extends projectile with ID tracking for integration with combat state
 */
export interface VisualProjectile {
  projectileId: string;
  sprite: PIXI.Graphics;
  type: ProjectileType;
  sourceX: number;
  sourceY: number;
  targetX: number;
  targetY: number;
  startTime: number;
  progress: number;
  isComplete: boolean;
}

/**
 * Timed projectile with server-synchronized timing (L4-RANGED-005)
 * Uses duration instead of speed for exact arrival timing
 */
export interface TimedProjectile extends VisualProjectile {
  /** Total travel time in milliseconds */
  durationMs: number;
}

/**
 * Parameters for spawning a timed projectile
 */
export interface TimedProjectileParams {
  /** Optional explicit projectile ID (auto-generated if not provided) */
  projectileId?: string;
  /** Explicit projectile type (overrides abilityId mapping) */
  type?: ProjectileType;
  /** Ability ID for automatic type mapping */
  abilityId?: string;
  /** Start position */
  source: { x: number; y: number };
  /** End position */
  target: { x: number; y: number };
  /** Travel duration in milliseconds */
  durationMs: number;
  /** Optional sprite frames for animated projectile (base64 PNG array) */
  spriteFrames?: string[];
  /** Element type for fallback color */
  element?: string;
  /** Multi-projectile volley parameters (L3-MULTI-PROJECTILE) */
  volleyData?: {
    /** Index of this projectile in the volley (0-based) */
    projectileIndex: number;
    /** Total projectiles in this volley */
    totalProjectiles: number;
    /** Distribution pattern (L3-PARTICLE-BREATH: added 'breath') */
    pattern: 'single' | 'fan' | 'volley' | 'barrage' | 'breath';
  };
}

/**
 * Sprite-based timed projectile with animation frames
 * Extends TimedProjectile with sprite-specific properties
 */
export interface SpriteTimedProjectile extends Omit<TimedProjectile, 'sprite'> {
  /** Display object (either PIXI.Sprite or PIXI.Graphics) */
  sprite: PIXI.Sprite | PIXI.Graphics;
  /** Whether this uses sprite frames (true) or generic graphics (false) */
  usesSprite: boolean;
  /** Textures for animation frames (if using sprite) */
  textures?: PIXI.Texture[];
  /** Current animation frame index */
  frameIndex?: number;
  /** Last frame update time */
  lastFrameTime?: number;
  /** Frame duration in ms */
  frameDurationMs?: number;
}

/**
 * Element to fallback color mapping
 */
const ELEMENT_FALLBACK_COLORS: Record<string, number> = {
  fire: 0xff4500,     // Orange-red
  ice: 0x87ceeb,      // Sky blue
  lightning: 0xffff00, // Yellow
  poison: 0x32cd32,   // Lime green
  physical: 0x8b4513, // Brown
  magic: 0x9400d3,    // Purple
  dark: 0x4b0082,     // Indigo
  holy: 0xffd700,     // Gold
  water: 0x1e90ff,    // Dodger blue
};

/**
 * Default configuration
 */
const DEFAULT_CONFIG: Required<ProjectileConfig> = {
  speed: 300,      // 300 pixels per second
  arcHeight: 20    // 20 pixel arc
};

/**
 * Calculate launch offset for multi-projectile volleys (L3-MULTI-PROJECTILE)
 * @param pattern - Distribution pattern type
 * @param projectileIndex - Index of this projectile in the volley (0-based)
 * @param totalProjectiles - Total number of projectiles in the volley
 * @returns Offset from the source position {x, y}
 */
function calculateLaunchOffset(
  pattern: 'single' | 'fan' | 'volley' | 'barrage' | 'breath',
  projectileIndex: number,
  totalProjectiles: number
): { x: number; y: number } {
  switch (pattern) {
    case 'fan': {
      // Spread angle: -30 to +30 degrees
      // For single projectile, use 0 degrees (center)
      const normalizedIndex = totalProjectiles > 1
        ? projectileIndex / (totalProjectiles - 1)
        : 0.5;
      const fanAngle = (normalizedIndex - 0.5) * 60 * (Math.PI / 180);
      return { x: Math.sin(fanAngle) * 15, y: -Math.cos(fanAngle) * 10 };
    }
    case 'breath': {
      // L3-PARTICLE-BREATH: Wide 60° arc spread for breath attacks
      // Creates dramatic cone of projectiles
      const normalizedIndex = totalProjectiles > 1
        ? projectileIndex / (totalProjectiles - 1)
        : 0.5;
      const breathAngle = (normalizedIndex - 0.5) * 60 * (Math.PI / 180);
      // Wider spread than fan, with slight random variation
      const seed = projectileIndex * 12345;
      const variation = ((seed * 9301 + 49297) % 233280) / 233280 - 0.5;
      return {
        x: Math.sin(breathAngle) * (8 + variation * 4),
        y: -Math.cos(breathAngle) * 10 + variation * 4
      };
    }
    case 'volley': {
      // Slight vertical offset for visual separation
      return { x: 0, y: projectileIndex * -3 };
    }
    case 'barrage': {
      // Random slight offset for organic feel
      // Use deterministic randomness based on index for consistency
      const seed = projectileIndex * 12345;
      const pseudoRandomX = ((seed * 9301 + 49297) % 233280) / 233280;
      const pseudoRandomY = ((seed * 9307 + 49301) % 233280) / 233280;
      return {
        x: (pseudoRandomX - 0.5) * 20,
        y: (pseudoRandomY - 0.5) * 10
      };
    }
    case 'single':
    default:
      return { x: 0, y: 0 };
  }
}

/**
 * Visual properties for each projectile type
 */
const PROJECTILE_PROPERTIES: Record<ProjectileType, ProjectileProperties> = {
  [ProjectileType.ARROW]: {
    color: 0x8b4513,  // Brown
    size: 12,
    hasArc: true
  },
  [ProjectileType.FIREBALL]: {
    color: 0xff4500,  // Orange-red
    size: 16,
    hasArc: true
  },
  [ProjectileType.ICE_SHARD]: {
    color: 0x87ceeb,  // Sky blue
    size: 10,
    hasArc: true
  },
  [ProjectileType.LIGHTNING]: {
    color: 0xffff00,  // Yellow
    size: 8,
    hasArc: false     // Lightning travels in straight line
  },
  [ProjectileType.GENERIC]: {
    color: 0xffffff,  // White
    size: 8,
    hasArc: true
  }
};

/**
 * ProjectileRenderer
 *
 * Service for creating and animating combat projectiles.
 * Handles arcing trajectories and rotation to face direction of travel.
 */
export class ProjectileRenderer {
  private config: Required<ProjectileConfig>;
  private projectileIdCounter = 0;

  constructor(config: ProjectileConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Generate unique projectile ID
   */
  private generateProjectileId(): string {
    return `proj-${Date.now()}-${++this.projectileIdCounter}`;
  }

  /**
   * Get current configuration
   */
  public getConfig(): Required<ProjectileConfig> {
    return { ...this.config };
  }

  /**
   * Get visual properties for a projectile type
   */
  public getProjectileProperties(type: ProjectileType): ProjectileProperties {
    return { ...PROJECTILE_PROPERTIES[type] };
  }

  /**
   * Calculate distance between two points
   */
  public calculateDistance(
    source: { x: number; y: number },
    target: { x: number; y: number }
  ): number {
    const dx = target.x - source.x;
    const dy = target.y - source.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Calculate angle from source to target
   */
  private calculateAngle(
    source: { x: number; y: number },
    target: { x: number; y: number }
  ): number {
    const dx = target.x - source.x;
    const dy = target.y - source.y;
    return Math.atan2(dy, dx);
  }

  /**
   * Create a new projectile
   */
  public createProjectile(
    type: ProjectileType,
    source: { x: number; y: number },
    target: { x: number; y: number }
  ): Projectile {
    const props = this.getProjectileProperties(type);
    const graphics = new PIXI.Graphics();

    // Draw projectile shape
    graphics.circle(0, 0, props.size / 2);
    graphics.fill({ color: props.color, alpha: 1.0 });

    // Position at source
    graphics.x = source.x;
    graphics.y = source.y;

    // Rotate to face target
    const angle = this.calculateAngle(source, target);
    graphics.rotation = angle;

    return {
      graphics,
      type,
      sourceX: source.x,
      sourceY: source.y,
      targetX: target.x,
      targetY: target.y,
      startTime: performance.now(),
      progress: 0,
      isComplete: false
    };
  }

  /**
   * Create a visual projectile with ID tracking for state-based rendering
   * This is used by the visualization manager to sync projectiles with combat state
   */
  public createVisualProjectile(
    projectileId: string,
    source: { x: number; y: number },
    target: { x: number; y: number },
    spritePath?: string
  ): VisualProjectile {
    // Determine projectile type from sprite path if provided
    const type = this.getProjectileTypeFromSpritePath(spritePath);
    const props = this.getProjectileProperties(type);
    const graphics = new PIXI.Graphics();

    // Draw projectile shape
    graphics.circle(0, 0, props.size / 2);
    graphics.fill({ color: props.color, alpha: 1.0 });

    // Position at source
    graphics.x = source.x;
    graphics.y = source.y;

    // Rotate to face target
    const angle = this.calculateAngle(source, target);
    graphics.rotation = angle;

    return {
      projectileId,
      sprite: graphics,
      type,
      sourceX: source.x,
      sourceY: source.y,
      targetX: target.x,
      targetY: target.y,
      startTime: performance.now(),
      progress: 0,
      isComplete: false
    };
  }

  /**
   * Map sprite path to projectile type
   * Used for state-based projectile creation
   */
  private getProjectileTypeFromSpritePath(spritePath?: string): ProjectileType {
    if (!spritePath) return ProjectileType.GENERIC;

    const path = spritePath.toLowerCase();
    if (path.includes('arrow')) return ProjectileType.ARROW;
    if (path.includes('fireball')) return ProjectileType.FIREBALL;
    if (path.includes('ice')) return ProjectileType.ICE_SHARD;
    if (path.includes('lightning')) return ProjectileType.LIGHTNING;

    return ProjectileType.GENERIC;
  }

  /**
   * Update projectile animation
   */
  public updateAnimation(projectile: Projectile, currentTime: number): void {
    const elapsed = currentTime - projectile.startTime;
    const distance = this.calculateDistance(
      { x: projectile.sourceX, y: projectile.sourceY },
      { x: projectile.targetX, y: projectile.targetY }
    );

    // Handle zero distance edge case
    if (distance === 0) {
      projectile.isComplete = true;
      projectile.progress = 1;
      return;
    }

    // Calculate travel time based on distance and speed
    const travelTime = (distance / this.config.speed) * 1000; // Convert to ms
    const progress = Math.min(elapsed / travelTime, 1.0);

    projectile.progress = progress;

    if (progress >= 1.0) {
      projectile.isComplete = true;
      projectile.graphics.x = projectile.targetX;
      projectile.graphics.y = projectile.targetY;
      return;
    }

    // Linear interpolation for X
    const x = projectile.sourceX + (projectile.targetX - projectile.sourceX) * progress;

    // Linear interpolation for Y with optional arc
    let y = projectile.sourceY + (projectile.targetY - projectile.sourceY) * progress;

    const props = this.getProjectileProperties(projectile.type);
    if (props.hasArc) {
      // Parabolic arc: peaks at midpoint (progress = 0.5)
      // Arc formula: -4 * arcHeight * progress * (progress - 1)
      // This gives 0 at progress=0 and progress=1, and max at progress=0.5
      const arcOffset = -4 * this.config.arcHeight * progress * (progress - 1);
      y -= arcOffset; // Subtract because Y increases downward in screen space
    }

    projectile.graphics.x = x;
    projectile.graphics.y = y;

    // Update rotation to face direction of travel (considering arc)
    if (progress > 0 && progress < 1) {
      // Calculate tangent of trajectory
      const nextProgress = Math.min(progress + 0.01, 1.0);
      const nextX = projectile.sourceX + (projectile.targetX - projectile.sourceX) * nextProgress;
      let nextY = projectile.sourceY + (projectile.targetY - projectile.sourceY) * nextProgress;

      if (props.hasArc) {
        const nextArcOffset = -4 * this.config.arcHeight * nextProgress * (nextProgress - 1);
        nextY -= nextArcOffset;
      }

      const angle = Math.atan2(nextY - y, nextX - x);
      projectile.graphics.rotation = angle;
    }
  }

  /**
   * Destroy projectile and cleanup resources
   * Handles both graphics-based and sprite-based projectiles
   */
  public destroyProjectile(projectile: Projectile | VisualProjectile | SpriteTimedProjectile): void {
    // Handle sprite-based projectiles with texture cleanup
    if ('usesSprite' in projectile && projectile.usesSprite) {
      this.destroySpriteProjectile(projectile as SpriteTimedProjectile);
      return;
    }

    // Handle standard projectiles
    if ('sprite' in projectile) {
      projectile.sprite.destroy();
    } else if ('graphics' in projectile) {
      projectile.graphics.destroy();
    }
  }

  // =====================================================
  // L4-RANGED-005: Timing-Based Projectile Methods
  // =====================================================

  /**
   * Map ability ID to projectile type
   * Used to determine visual style from combat ability
   */
  public mapAbilityIdToProjectileType(abilityId: string): ProjectileType {
    const id = abilityId.toLowerCase();

    // Fire abilities
    if (id.includes('fire') || id.includes('flame')) {
      return ProjectileType.FIREBALL;
    }

    // Ice abilities
    if (id.includes('ice') || id.includes('frost')) {
      return ProjectileType.ICE_SHARD;
    }

    // Lightning abilities
    if (id.includes('lightning') || id.includes('shock')) {
      return ProjectileType.LIGHTNING;
    }

    // Physical ranged
    if (id.includes('bow') || id.includes('arrow') || id.includes('crossbow')) {
      return ProjectileType.ARROW;
    }

    // Default fallback
    return ProjectileType.GENERIC;
  }

  /**
   * Calculate duration in milliseconds from tick delta
   * @param tickDelta Number of ticks for projectile travel
   * @param ticksPerSecond Server tick rate (default: 60)
   */
  public calculateDurationFromTicks(tickDelta: number, ticksPerSecond = 60): number {
    return (tickDelta / ticksPerSecond) * 1000;
  }

  /**
   * Spawn a projectile with timing-based movement (L4-RANGED-005)
   * Projectile will arrive at target after exactly durationMs milliseconds
   * Used for synchronizing visual projectiles with server damage timing
   *
   * If spriteFrames are provided, creates an animated PIXI.Sprite.
   * Otherwise falls back to a colored PIXI.Graphics circle.
   *
   * ENHANCED (L3-MULTI-PROJECTILE): Supports multi-projectile volleys with pattern-based offsets
   */
  public spawnProjectileWithTiming(params: TimedProjectileParams): SpriteTimedProjectile {
    // Determine projectile type
    const type = params.type ?? this.mapAbilityIdToProjectileType(params.abilityId ?? 'generic');
    const props = this.getProjectileProperties(type);

    // Calculate launch offset for multi-projectile volleys (L3-MULTI-PROJECTILE)
    let sourceX = params.source.x;
    let sourceY = params.source.y;

    if (params.volleyData) {
      const offset = calculateLaunchOffset(
        params.volleyData.pattern,
        params.volleyData.projectileIndex,
        params.volleyData.totalProjectiles
      );
      sourceX += offset.x;
      sourceY += offset.y;
    }

    // Calculate angle to target (from potentially offset source)
    const angle = this.calculateAngle({ x: sourceX, y: sourceY }, params.target);

    // Try to create sprite-based projectile if frames provided
    if (params.spriteFrames && params.spriteFrames.length > 0) {
      try {
        const textures = this.createTexturesFromFrames(params.spriteFrames);

        if (textures.length > 0) {
          const sprite = new PIXI.Sprite(textures[0]);
          sprite.anchor.set(0.5, 0.5);
          sprite.x = sourceX;
          sprite.y = sourceY;
          sprite.rotation = angle;

          // Scale sprite to appropriate projectile size (32x32 base, scale as needed)
          const targetSize = 32;
          sprite.width = targetSize;
          sprite.height = targetSize;

          const frameDurationMs = params.durationMs / (textures.length * 2); // Loop through frames twice during flight

          return {
            projectileId: params.projectileId ?? this.generateProjectileId(),
            sprite,
            type,
            sourceX: sourceX,
            sourceY: sourceY,
            targetX: params.target.x,
            targetY: params.target.y,
            startTime: performance.now(),
            progress: 0,
            isComplete: false,
            durationMs: params.durationMs,
            usesSprite: true,
            textures,
            frameIndex: 0,
            lastFrameTime: performance.now(),
            frameDurationMs: Math.max(frameDurationMs, 50), // Min 50ms per frame
          };
        }
      } catch (error) {
        console.warn('[ProjectileRenderer] Failed to create sprite projectile, falling back to graphics:', error);
      }
    }

    // Fallback: Create graphics-based projectile
    const graphics = new PIXI.Graphics();

    // Use element color if available, otherwise use type color
    const color = params.element
      ? (ELEMENT_FALLBACK_COLORS[params.element] ?? props.color)
      : props.color;

    graphics.circle(0, 0, props.size / 2);
    graphics.fill({ color, alpha: 1.0 });

    // Position at source (with volley offset applied)
    graphics.x = sourceX;
    graphics.y = sourceY;

    // Rotate to face target
    graphics.rotation = angle;

    return {
      projectileId: params.projectileId ?? this.generateProjectileId(),
      sprite: graphics,
      type,
      sourceX: sourceX,
      sourceY: sourceY,
      targetX: params.target.x,
      targetY: params.target.y,
      startTime: performance.now(),
      progress: 0,
      isComplete: false,
      durationMs: params.durationMs,
      usesSprite: false,
    };
  }

  /**
   * Create PIXI textures from base64 PNG frames
   */
  private createTexturesFromFrames(frames: string[]): PIXI.Texture[] {
    const textures: PIXI.Texture[] = [];

    for (const frame of frames) {
      try {
        // Handle both with and without data URI prefix
        const dataUri = frame.startsWith('data:')
          ? frame
          : `data:image/png;base64,${frame}`;

        const texture = PIXI.Texture.from(dataUri);
        textures.push(texture);
      } catch (error) {
        console.warn('[ProjectileRenderer] Failed to create texture from frame:', error);
      }
    }

    return textures;
  }

  /**
   * Update timed projectile animation (L4-RANGED-005)
   * Uses durationMs for timing instead of speed-based calculation
   * Supports both sprite-based and graphics-based projectiles
   */
  public updateTimedProjectile(projectile: TimedProjectile | SpriteTimedProjectile, currentTime: number): void {
    const elapsed = currentTime - projectile.startTime;
    const distance = this.calculateDistance(
      { x: projectile.sourceX, y: projectile.sourceY },
      { x: projectile.targetX, y: projectile.targetY }
    );

    // Handle zero distance edge case
    if (distance === 0) {
      projectile.isComplete = true;
      projectile.progress = 1;
      return;
    }

    // Calculate progress based on duration (not speed)
    const progress = Math.min(elapsed / projectile.durationMs, 1.0);
    projectile.progress = progress;

    if (progress >= 1.0) {
      projectile.isComplete = true;
      projectile.sprite.x = projectile.targetX;
      projectile.sprite.y = projectile.targetY;
      return;
    }

    // Linear interpolation for X
    const x = projectile.sourceX + (projectile.targetX - projectile.sourceX) * progress;

    // Linear interpolation for Y with optional arc
    let y = projectile.sourceY + (projectile.targetY - projectile.sourceY) * progress;

    const props = this.getProjectileProperties(projectile.type);
    if (props.hasArc) {
      // Parabolic arc: peaks at midpoint (progress = 0.5)
      const arcOffset = -4 * this.config.arcHeight * progress * (progress - 1);
      y -= arcOffset; // Subtract because Y increases downward in screen space
    }

    projectile.sprite.x = x;
    projectile.sprite.y = y;

    // Update rotation to face direction of travel (considering arc)
    if (progress > 0 && progress < 1) {
      const nextProgress = Math.min(progress + 0.01, 1.0);
      const nextX = projectile.sourceX + (projectile.targetX - projectile.sourceX) * nextProgress;
      let nextY = projectile.sourceY + (projectile.targetY - projectile.sourceY) * nextProgress;

      if (props.hasArc) {
        const nextArcOffset = -4 * this.config.arcHeight * nextProgress * (nextProgress - 1);
        nextY -= nextArcOffset;
      }

      const tangentAngle = Math.atan2(nextY - y, nextX - x);
      projectile.sprite.rotation = tangentAngle;
    }

    // Update sprite animation frames if this is a sprite-based projectile
    if (this.isSpriteProjectile(projectile) && projectile.usesSprite) {
      this.updateSpriteAnimation(projectile, currentTime);
    }
  }

  /**
   * Type guard for SpriteTimedProjectile
   */
  private isSpriteProjectile(projectile: TimedProjectile | SpriteTimedProjectile): projectile is SpriteTimedProjectile {
    return 'usesSprite' in projectile;
  }

  /**
   * Update sprite animation frames
   */
  private updateSpriteAnimation(projectile: SpriteTimedProjectile, currentTime: number): void {
    if (!projectile.textures || !projectile.textures.length || projectile.frameIndex === undefined) {
      return;
    }

    const timeSinceLastFrame = currentTime - (projectile.lastFrameTime ?? currentTime);
    const frameDuration = projectile.frameDurationMs ?? 100;

    if (timeSinceLastFrame >= frameDuration) {
      // Advance to next frame
      projectile.frameIndex = (projectile.frameIndex + 1) % projectile.textures.length;
      projectile.lastFrameTime = currentTime;

      // Update sprite texture
      if (projectile.sprite instanceof PIXI.Sprite) {
        projectile.sprite.texture = projectile.textures[projectile.frameIndex];
      }
    }
  }

  /**
   * Destroy a sprite-based projectile and cleanup textures
   */
  public destroySpriteProjectile(projectile: SpriteTimedProjectile): void {
    // Destroy textures if this was a sprite projectile
    if (projectile.textures) {
      for (const texture of projectile.textures) {
        texture.destroy(true);
      }
    }

    // Destroy the sprite/graphics
    projectile.sprite.destroy();
  }
}
